import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CSATSetting {
  id: string;
  page_id: string;
  page_name: string;
  page_path: string;
  is_enabled: boolean;
  display_days: number;
  created_at: string;
  updated_at: string;
}

export const useCSATSettings = () => {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['csat-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('csat_settings')
        .select('*')
        .order('page_name');

      if (error) throw error;
      return data as CSATSetting[];
    },
  });

  const updateSetting = useMutation({
    mutationFn: async ({ 
      pageId, 
      isEnabled, 
      displayDays,
      showToast = true
    }: { 
      pageId: string; 
      isEnabled?: boolean; 
      displayDays?: number;
      showToast?: boolean;
    }) => {
      const updateData: any = {};
      if (isEnabled !== undefined) updateData.is_enabled = isEnabled;
      if (displayDays !== undefined) updateData.display_days = displayDays;

      const { data, error } = await supabase
        .from('csat_settings')
        .update(updateData)
        .eq('page_id', pageId)
        .select()
        .single();

      if (error) throw error;
      return { data, showToast };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['csat-settings'] });
      if (result.showToast) {
        toast.success('Configuração salva automaticamente');
      }
    },
    onError: (error) => {
      console.error('Erro ao salvar configuração CSAT:', error);
      toast.error('Erro ao salvar configuração');
    },
  });

  const updateAllSettings = useMutation({
    mutationFn: async ({ 
      isEnabled, 
      displayDays,
      pageIds
    }: { 
      isEnabled?: boolean; 
      displayDays?: number;
      pageIds: string[];
    }) => {
      const updateData: any = {};
      if (isEnabled !== undefined) updateData.is_enabled = isEnabled;
      if (displayDays !== undefined) updateData.display_days = displayDays;

      const { data, error } = await supabase
        .from('csat_settings')
        .update(updateData)
        .in('page_id', pageIds)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['csat-settings'] });
      toast.success('Configurações salvas automaticamente');
    },
    onError: (error) => {
      console.error('Erro ao salvar configurações CSAT:', error);
      toast.error('Erro ao salvar configurações');
    },
  });

  return {
    settings,
    isLoading,
    updateSetting: updateSetting.mutate,
    updateAllSettings: updateAllSettings.mutate,
  };
};
